/*eslint no-console: 0*/

import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {getChannelOptions} from './channelHandlers';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import {injectIntl, IntlShape} from 'react-intl';
import meQuery from '../queries/MeQuery';
import {renderLoadingPanel} from '../loadingLine/renderLoadingPanel.react';
import {getLastOrganisationId} from '../app/LastOrganisationId';
import { User } from '../../common/types/types';

interface ChannelViewProps {
 cancel?: (...args: unknown[]) => unknown | boolean;
 connect?: boolean;
 createCallback?: (...args: unknown[]) => unknown;
 intl?: object;
 loadingStopFn?: (...args: unknown[]) => unknown;
 relay?: object;
 routes?: unknown[];
 step?: number;
 user: User;
 wrapper?: boolean;
}

class ChannelView extends React.Component<ChannelViewProps> {
    static propTypes = {
        cancel: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
        connect: PropTypes.bool,
        createCallback: PropTypes.func,
        intl: PropTypes.object,
        loadingStopFn: PropTypes.func,
        relay: PropTypes.object,
        routes: PropTypes.array,
        step: PropTypes.number,
        user: PropTypes.object.isRequired,
        wrapper: PropTypes.bool
    };

    static defaultProps = {
        wrapper: false
    };

    static contextTypes = {
        router: PropTypes.object
    };

    constructor(props) {
        super(props);

        let isConnection = false;
        if (props.connect || props.routes && props.routes[props.routes.length - 1].path === "add-step/2/:channelId/") {
            isConnection = true;
        }

        this.state = {
            isConnection: isConnection,
            isLoading: !Boolean(props.loadingStopFn)
        };

        this.timestamp = String(Date.now());
    }

    render() {
        let optionEditHandler = getChannelOptions(this.props.user.organisation.channel.channelDef);

        if (!Relay.isContainer(optionEditHandler)) {
            throw new Error('The channel view expects only Relay.container for now.');
        }

        const queryConfig = {
            queries: meQuery,
            name: 'User',
            params: {
                organisationId: this.props.user.organisation.realId,
                channelId: this.props.user.organisation.channel.realId,
                scenarioSelected: Boolean(this.props.user.scenario),
                timestamp: this.timestamp
            }
        };

        if (queryConfig.params.scenarioSelected) {
            queryConfig.params.scenarioId = this.props.user.scenario.realId;
        }

        return (
            <div>
                <Relay.Renderer
                    Container={optionEditHandler}
                    environment={Relay.Store}
                    onReadyStateChange={({aborted, done, error}) => {
                        if ((aborted || done || error) && this.state.isLoading) {
                            setTimeout(() => {
                                this.setState({
                                    isLoading: false
                                });
                            }, 0);
                        }
                    }}
                    queryConfig={queryConfig}
                    render={(state) => {
                        if (state.done) {
                            if (this.props.loadingStopFn) {
                                this.props.loadingStopFn();
                            }

                            return React.createElement(optionEditHandler, {...state.props, connect: this.state.isConnection, cancel: this.props.cancel, createCallback: this.props.createCallback, wrapper: this.props.wrapper});
                        }
                    }}
                />
                { this.state.isLoading ? renderLoadingPanel() : null }
            </div>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(ChannelView), (props) => {
    return props.user.organisation && props.user.organisation.channel;
}), {
    initialVariables: {
        organisationId: '',
        channelId: '',
        scenarioId: '',
        scenarioSelected: false,
    },
    prepareVariables (variables) {
        if (!variables.organisationId) {
            variables.organisationId = getLastOrganisationId();
        }

        variables['scenarioSelected'] = variables['scenarioId'] ? true : false;

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                featureFlags,
                scenario(id: $scenarioId) @include(if: $scenarioSelected) {
                    id,
                    realId,
                    urlFragment,
                },
                organisation(id: $organisationId) {
                    id,
                    realId,
                    channel(id: $channelId) {
                        realId,
                        channelDef {
                            realId,
                            type,
                            requiresAgent,
                            canUseAgent
                        }
                    }
                }
            }
        `,
    },
});
